import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { FileText, Download, ShoppingCart, Loader2, ExternalLink, Star, BookOpen, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JournalsPage = () => {
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPurchaseOptions, setShowPurchaseOptions] = useState(false);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchJournals();
  }, []);

  const fetchJournals = async () => {
    try {
      const response = await axios.get(`${API_URL}/journals`, {
        headers: getAuthHeader()
      });
      // Sort to put featured journal first
      const sorted = response.data.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
      setJournals(sorted);
    } catch (error) {
      console.error('Failed to fetch journals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (journal) => {
    if (!journal.download_url) {
      toast.info(`${journal.title} is available for print purchase only`);
      return;
    }
    // Simple approach - just open the URL (same as Companion Journal)
    window.open(journal.download_url, '_blank');
    toast.success(`Opening ${journal.title}...`);
  };

  const handleBuyPrint = (journal) => {
    if (journal.print_purchase_url) {
      window.open(journal.print_purchase_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const featuredJournal = journals.find(j => j.is_featured);
  const otherJournals = journals.filter(j => !j.is_featured);

  return (
    <div className="space-y-8" data-testid="journals-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900 mb-2">Journals & Workbooks</h1>
        <p className="text-stone-600">Download for free or purchase printed copies</p>
      </div>

      {/* Featured Companion Journal */}
      {featuredJournal && (
        <div className="card-cosmic overflow-hidden border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5" data-testid="featured-journal">
          <div className="grid md:grid-cols-2">
            <div className="aspect-video md:aspect-auto relative overflow-hidden">
              <img 
                src={featuredJournal.thumbnail_url}
                alt={featuredJournal.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4">
                <div className="bg-gradient-to-r from-primary to-accent text-white px-3 py-1 rounded-full flex items-center gap-2 text-sm font-semibold">
                  <Star className="w-4 h-4" />
                  Companion to the Ebook
                </div>
              </div>
            </div>
            
            <div className="p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-primary mb-2">
                <BookOpen className="w-5 h-5" />
                <span className="text-sm font-medium">Recommended</span>
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-3">{featuredJournal.title}</h3>
              <div className="text-stone-600 mb-6 whitespace-pre-line">{featuredJournal.description}</div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                {featuredJournal.download_url && (
                  <a
                    href={`${API_URL}/journals/${featuredJournal.id}/download`}
                    className="flex-1 btn-cosmic inline-flex items-center justify-center gap-2 text-center"
                    data-testid="download-featured-journal"
                  >
                    <Download className="w-4 h-4" />
                    Download Free PDF
                  </a>
                )}
                
                {/* Multiple purchase options */}
                {featuredJournal.purchase_options && featuredJournal.purchase_options.length > 0 ? (
                  <div className="relative">
                    <Button
                      onClick={() => setShowPurchaseOptions(!showPurchaseOptions)}
                      variant="outline"
                      className="flex items-center justify-center gap-2 border-2 border-accent text-accent hover:bg-accent/10 w-full sm:w-auto"
                      data-testid="buy-print-dropdown"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Buy Printed Copy
                      <ChevronDown className={`w-4 h-4 transition-transform ${showPurchaseOptions ? 'rotate-180' : ''}`} />
                    </Button>
                    
                    {showPurchaseOptions && (
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-stone-200 p-2 z-50">
                        {featuredJournal.purchase_options.map((option, index) => (
                          <a
                            key={index}
                            href={option.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full text-left px-4 py-3 rounded-lg hover:bg-stone-50 flex items-center gap-3 transition-colors"
                            data-testid={`purchase-option-${index}`}
                          >
                            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                              <BookOpen className="w-5 h-5 text-accent" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-stone-900">{option.name}</p>
                              <p className="text-xs text-stone-500">{option.description}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-stone-400" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ) : featuredJournal.print_purchase_url && (
                  <a
                    href={featuredJournal.print_purchase_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-accent text-accent hover:bg-accent/10 rounded-lg font-medium"
                    data-testid="buy-featured-print"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Buy Printed Copy
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Other Journals */}
      {otherJournals.length > 0 && (
        <>
          <h2 className="text-xl font-bold text-stone-900">More Journals</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {otherJournals.map((journal) => (
              <div key={journal.id} className="card-cosmic overflow-hidden card-hover" data-testid={`journal-card-${journal.id}`}>
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={journal.thumbnail_url}
                    alt={journal.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="bg-white/90 backdrop-blur px-3 py-1 rounded-full flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-stone-900">
                        {journal.is_coming_soon ? 'Coming Soon' : journal.download_url ? 'Free Download' : 'Print Only'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-stone-900 mb-2">{journal.title}</h3>
                  <div className="text-stone-600 mb-6 whitespace-pre-line">{journal.description}</div>
                  
                  <div className="flex gap-3">
                    {journal.is_coming_soon ? (
                      <div className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-stone-100 text-stone-500 rounded-lg font-medium cursor-not-allowed">
                        Coming Soon
                      </div>
                    ) : journal.download_url ? (
                      <a
                        href={`${API_URL}/journals/${journal.id}/download`}
                        className="flex-1 btn-cosmic inline-flex items-center justify-center gap-2"
                        data-testid={`download-btn-${journal.id}`}
                      >
                        <Download className="w-4 h-4" />
                        Download Free
                      </a>
                    ) : null}
                    {!journal.is_coming_soon && journal.print_purchase_url && (
                      <a
                        href={journal.print_purchase_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={journal.download_url 
                          ? "inline-flex items-center justify-center gap-2 px-4 py-2 border-2 border-accent text-accent hover:bg-accent/10 rounded-lg font-medium"
                          : "flex-1 btn-cosmic inline-flex items-center justify-center gap-2"
                        }
                        data-testid={`buy-print-btn-${journal.id}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Buy Print
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {journals.length === 0 && (
        <div className="card-cosmic p-12 text-center">
          <FileText className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <p className="text-stone-600">No journals available yet. Check back soon!</p>
        </div>
      )}
    </div>
  );
};

export default JournalsPage;
